/**
 * 操作の記録（LabRun）を進める・戻す。UI の「1 手戻す（⌘Z）」と、予想・再現の盤面の列に使う。
 */
import type { ApplyResult, LabModel, LabRun } from './types';

export function startRun<S>(initial: S): LabRun<S> {
  return { initial, steps: [] };
}

export function currentState<S>(run: LabRun<S>): S {
  return run.steps.at(-1)?.state ?? run.initial;
}

/** 操作を 1 つ適用する。操作できなかったとき（entry が null）は run を変えない。 */
export function applyOp<S, O>(
  model: LabModel<S, O>,
  run: LabRun<S>,
  op: O,
): { run: LabRun<S>; result: ApplyResult<S> } {
  const result = model.apply(currentState(run), op);
  if (!result.entry) return { run, result };
  return {
    run: {
      initial: run.initial,
      steps: [...run.steps, { entry: result.entry, state: result.state }],
    },
    result,
  };
}

/** 最後の 1 手を取り消す。手が無ければそのまま。 */
export function undoOp<S>(run: LabRun<S>): LabRun<S> {
  if (run.steps.length === 0) return run;
  return { initial: run.initial, steps: run.steps.slice(0, -1) };
}

/** 操作列を最初から順に適用する（予想の問題、テスト用）。操作できない手は飛ばす。 */
export function runOps<S, O>(model: LabModel<S, O>, initial: S, ops: readonly O[]): LabRun<S> {
  let run = startRun(initial);
  for (const op of ops) run = applyOp(model, run, op).run;
  return run;
}

/** 盤面の列（初期状態 + 各手の後）。 */
export function statesOf<S>(run: LabRun<S>): S[] {
  return [run.initial, ...run.steps.map((s) => s.state)];
}
